import businessDays from 'business-days-js';
import { getDateFromMongoId } from './EntriesHelper';
import { getWeekStartDate } from './CalendarHelper';
import { getNumDaysInBetween } from './WeekReview/WeekReviewHelper';

const businessDaysUS = businessDays({ excludeHolidays: ["columbus day", "veterans day"] });

/**
 * Calculates the longest streak of consecutive days where flows were taken. The smallest streak considered is 2 days in a row.
 * @param {Array} flowEntries - An array of flow entries (The DB representation of a flow) sorted by date from oldest to newest
 * @returns {Object|null} {streakLength: Number, startDate: Date, endDate: Date, active: true or false}, or `null` if no streak is found.  
 */
export function getLongestStreakOfDays(flowEntries, today) {
    if (!flowEntries || flowEntries.length < 2) return null;

    let longestStreak = 0;
    let currentStreak = 1;
    let longestStreakStart = null;
    let longestStreakEnd = null;

    let currentStart = getDateFromMongoId(flowEntries[0]._id) // Start of the current streak

    for (let i = 1; i < flowEntries.length; i++) {
        const prevDate = getDateFromMongoId(flowEntries[i-1]._id)
        const currentDate = getDateFromMongoId(flowEntries[i]._id)

        const daysBetween = businessDaysUS.countDays(prevDate, currentDate).totalDays;

        if (daysBetween === 0) {
            // Both flows on same day, skip
            continue;
        }
        else if (daysBetween === 1) {
            currentStreak += 1;
            // check if it's the longest
            if (currentStreak >= longestStreak) {
                longestStreak = currentStreak;
                longestStreakStart = currentStart;
                longestStreakEnd = currentDate;
            }
        }
        else {
            // Reset current streak
            currentStreak = 1;
            currentStart = currentDate;
        }
    }

    if (longestStreak < 2) {
        return null;
    }

    const isLongestStreakActive = businessDaysUS.countDays(longestStreakEnd, today ?? new Date()).totalDays <= 1;

    return {
        streakLength: longestStreak,
        startDate: longestStreakStart,
        endDate: longestStreakEnd,
        active: isLongestStreakActive
    };
}

/**
 * Determines if a flow streak is currently active and returns null if not.
 * @param today A date that must be >= last flow's date. If not passed in, defaults to new Date()
 * @returns {Object} {streakLength: <number>, <streakStart>: <date> when streak started}
 */
export function getActiveStreak(flowEntries, today) {
    if (!flowEntries || flowEntries.length < 1) return null;

    const activeDate = today ?? new Date();
    const lastFlowDate = getDateFromMongoId(flowEntries[flowEntries.length - 1]._id);
    // Check if a streak is currently active
    const daysBetweenTodayAndLastFlow = businessDaysUS.countDays(lastFlowDate, activeDate).totalDays;
    if (daysBetweenTodayAndLastFlow > 1) {
        return null;
    }

    let currentStreak = 1;
    let streakStart = lastFlowDate;

    for (let i = flowEntries.length - 1; i > 0; i--) {
        const prevDate = getDateFromMongoId(flowEntries[i-1]._id);
        const currentDate = getDateFromMongoId(flowEntries[i]._id);

        const daysBetween = businessDaysUS.countDays(prevDate, currentDate).totalDays;

        if (daysBetween === 0) {
            // Both flows on same day, skip
            continue;
        }
        else if (daysBetween === 1) {
            currentStreak += 1;
            streakStart = prevDate;
        }
        else {
            // streak is broken
            break;
        }
    }
    return {
        streakLength: currentStreak,
        startDate: streakStart,
    };
}

/**
 * Habit hero is only given once a week, to calculate the streak we look back 7 days
 * @param {Array} flowEntries - An array of flow entries (The DB representation of a flow) sorted by date from 
 * oldest to newest, where the last flow should be no newer than the date passed in.
 * @param {Date} today - if not passed in, today will be set to the current Date
 * @returns {Number|null} null if no streak found. Streak means at least 2 recurrences.
 */
export function getHabitHeroStreak(flowEntries, today) {
    if (!flowEntries || flowEntries.length < 10) return null;

    let streak = 0;
    // Find the first reward in the current week
    let lastRewardDate = null;
    let latestRewardIndex;
    const currentWeekStartDate = getWeekStartDate(today ?? new Date());
    for (let i = flowEntries.length - 1; i >= 0; i--) {
        const flowDate = getDateFromMongoId(flowEntries[i]._id);
        if (currentWeekStartDate < flowDate && getNumDaysInBetween(currentWeekStartDate, flowDate) <= 7) {
            if (flowEntries[i].quantitativeRewards?.includes('HabitHero')) {
                streak = 1; // Start streak
                lastRewardDate = flowDate;
                latestRewardIndex = i;
                break;
            }
        }
        else {
            break;
        }
    }

    // If no reward in the current week, return null
    if (!lastRewardDate) return null;
  
    // Look for consecutive rewards exactly 7 days apart
    for (let i = latestRewardIndex - 1; i >= 0; i--) {
        if (flowEntries[i].quantitativeRewards?.includes('HabitHero')) {
            const flowDate = getDateFromMongoId(flowEntries[i]._id);
            if (getNumDaysInBetween(flowDate, lastRewardDate) <= 7) {
                streak++;
                lastRewardDate = flowDate;
            }
            else {
                break;
            }
        }
    }
  
    return streak;
}